/**
 * Draft ↔ API mapping for the container editor. apiToDraft(persisted) is
 * also the dirty baseline, so the mapping must be stable (same key order,
 * same defaults) — a refetch that changes nothing must compare equal.
 * For Media Devoted | Created By Ciro Autiero
 */
import { DROP_GEN_WEEKDAYS, type DropGenEffectiveConfig, type DropGenOwnSections, normalizeRunDays, parseListRanking, readHourOptimizer } from '@trafficpulse/shared';

import type {
  ContainerDraft,
  DropGenContainerDetail,
} from '../../../drop-generator.types';
import { DEFAULT_HOUR_OPTIMIZER } from '../../../drop-generator.types';
import { DEFAULT_SEND, pickSendDefaults, rawSendDefaults } from './satellites/send-defaults';
import { creativeToDraft } from './creative-draft';
import { verdictToDraft } from './satellites/verdict-draft';
import { scalingToDraft } from './satellites/scaling-draft';
import { gatewayVerdictToDraft } from './satellites/gateway-verdict-draft';
import { hitAgainToDraft } from './satellites/hit-again-draft';
import { gatewayToDraft } from './gateway-draft';
import { autoSendSeed } from '../autosend/autosend-slice';
import { slotToDraft } from './slots/slot-ops';

/** Phase 20 — the sections a follow (PATCH :id/master) carries: never
 *  auto-send — that section has one writer, PUT :id/autosend {own}, which the
 *  editor's Save sends after the follow (autosend-slice). */
export function draftOwnSections(own: DropGenOwnSections | undefined): DropGenOwnSections {
  const rest = { ...(own ?? {}) };
  delete rest.autoSend;
  return rest;
}

export function emptyDraft(): ContainerDraft {
  return {
    kind: 'CONTAINER',
    masterId: null,
    ownSections: {},
    sendDefaultsRaw: {},
    name: '',
    color: '#6366f1',
    campaignWiserId: null,
    staticTagId: null,
    enabled: true,
    startMode: 'WINDOW_OPEN', // "After the one before" left the choices (15/10/2026)
    gapMin: 0,
    startAtMin: null,
    endAtMin: null,
    intervalMin: 30,
    runDays: [...DROP_GEN_WEEKDAYS],
    weekendAmountPct: 100,
    sendOutsideWindow: false,
    defaultAmount: 1000,
    failMode: 'STOP_FOR_DAY',
    hourOptimizer: { ...DEFAULT_HOUR_OPTIMIZER },
    slots: [],
    groups: [],
    gateway: gatewayToDraft(null),
    creative: creativeToDraft(null),
    verdict: verdictToDraft(null),
    scalingEnabled: false,
    scaling: scalingToDraft(null),
    gatewayVerdict: gatewayVerdictToDraft(null),
    hitAgain: hitAgainToDraft(null),
    listOverrides: [],
    sendDefaults: { ...DEFAULT_SEND },
    ...autoSendSeed(null),
  };
}

/** Every section is seeded from what the container RUNS WITH (its own rows
 *  when standalone or own, the master's when inherited — Phase 14), so
 *  hints, summaries and read-only rendering need no second code path. */
export function apiToDraft(detail: DropGenContainerDetail): ContainerDraft {
  const eff = effectiveOf(detail);
  const defaults = (eff.creative?.dropDefaultsJson ?? {}) as Record<
    string,
    unknown
  >;
  const optimizer = (eff.hourOptimizerJson ?? {}) as Record<string, unknown>;
  return {
    kind: detail.kind ?? 'CONTAINER',
    masterId: detail.masterId ?? null,
    ownSections: detail.ownSections ?? {},
    ...autoSendSeed(detail.autoSend),
    sendDefaultsRaw: rawSendDefaults(defaults),
    name: detail.name,
    color: detail.color,
    campaignWiserId: eff.campaignWiserId,
    staticTagId: detail.staticTagId ?? null,
    enabled: detail.enabled,
    startMode: detail.startMode,
    gapMin: detail.gapMin,
    startAtMin: detail.startAtMin,
    endAtMin: detail.endAtMin,
    intervalMin: detail.intervalMin,
    runDays: normalizeRunDays(detail.runDays),
    weekendAmountPct: detail.weekendAmountPct ?? 100,
    sendOutsideWindow: detail.sendOutsideWindow ?? false,
    defaultAmount: eff.defaultAmount,
    failMode: eff.failMode,
    hourOptimizer: readHourOptimizer(optimizer),
    slots: detail.slots.map(slotToDraft),
    groups: detail.groups.map((g) => ({
      listGroupId: g.listGroupId,
      orderMode: g.orderMode === 'INHERIT' ? 'MANUAL' : g.orderMode, // "Biggest first" hidden 14/10/2026 — same order
      manualOrderJson: g.manualOrderJson ?? null,
      orderFormulaJson: parseListRanking(g.orderFormulaJson ?? null),
      listsPerDay: g.listsPerDay ?? 1,
      allListsMode: g.allListsMode ?? 'EACH',
    })),
    gateway: gatewayToDraft(eff.gateway),
    creative: creativeToDraft(eff.creative),
    verdict: verdictToDraft(eff.verdict),
    scalingEnabled: eff.scalingEnabled,
    scaling: scalingToDraft(eff.verdict),
    gatewayVerdict: gatewayVerdictToDraft(eff.verdict),
    hitAgain: hitAgainToDraft(eff.verdict),
    listOverrides: detail.listOverrides ?? [],
    sendDefaults: {
      ...DEFAULT_SEND,
      ...pickSendDefaults(defaults),
    },
  };
}

/** Old cached payloads (before Phase 14) have no `effective`: the row is itself. */
function effectiveOf(detail: DropGenContainerDetail): DropGenEffectiveConfig {
  return (
    detail.effective ?? {
      campaignWiserId: detail.campaignWiserId,
      defaultAmount: detail.defaultAmount,
      failMode: detail.failMode,
      scalingEnabled: detail.scalingEnabled,
      hourOptimizerJson: detail.hourOptimizerJson,
      gateway: detail.gateway,
      creative: detail.creative,
      verdict: detail.verdict,
      sources: {} as DropGenEffectiveConfig['sources'],
    }
  );
}

export { draftToContainerBody, masterBody, validateDraft } from './master/container-body';

export { DEFAULT_SEND, pickSendDefaults, rawSendDefaults } from './satellites/send-defaults';
